import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ExternalLink,
  Brain,
  Cpu,
  Atom,
  Play,
  Newspaper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

// ── Consciousness research publications ──────────────────────────────────
const consciousnessPublications = [
  { type: "conference", authors: [], year: 2024, title: "The irreducibility of consciousness in human intelligence: Implications for AI, legal accountability, and the human-in-the-loop approach", venue: "2024 IEEE Conference on Engineering Informatics (ICEI), pp. 1–10", url: "https://doi.org/10.1109/ICEI64305.2024.10912272" },
  { type: "conference", authors: [], year: 2023, title: "Unifying matter, energy and consciousness", venue: "11th International Conference on Mathematical Modeling in Physical Sciences · AIP Conference Proceedings, 2872(1), 110001", url: "https://doi.org/10.1063/5.0162815" },
  { type: "conference", authors: [], year: 2024, title: "Mathematical modeling of consciousness for unifying causation, relativity and quantum mechanics", venue: "12th International Conference on Mathematical Modeling in Physical Sciences · Journal of Physics: Conference Series, 2701(1), 012051", url: "https://doi.org/10.1088/1742-6596/2701/1/012051" },
  { type: "conference", authors: [], year: 2024, title: "Conscious model of particle physics: The grand theory unifying local and non-local realities", venue: "12th International Conference on Mathematical Modeling in Physical Sciences · Journal of Physics: Conference Series, 2701(1), 012110", url: "https://doi.org/10.1088/1742-6596/2701/1/012110" },
  { type: "conference", authors: [], year: 2024, title: "The model of consciousness: An analysis of causation, self, Gödel's incompleteness, and Buddhist philosophy", venue: "13th International Conference on Mathematical Modeling in Physical Sciences", url: "https://doi.org/10.1088/1742-6596/3027/1/012014" },
  { type: "conference", authors: [], year: 2024, title: "The mind, experience, observation, and reality: A framework and technique for fundamental experience of Rūpa realm", venue: "13th International Conference on Mathematical Modeling in Physical Sciences", url: "https://doi.org/10.1088/1742-6596/3027/1/012012" },
  { type: "conference", authors: [], year: 2024, title: "Consciousness and mathematics: A number theoretic approach to modelling reality", venue: "13th International Conference on Mathematical Modeling in Physical Sciences", url: "https://doi.org/10.1088/1742-6596/3027/1/012013" },
];

// ── AI governance publications ───────────────────────────────────────────
const aiGovernancePublications = [
  { type: "submission", authors: [], year: 2022, title: "AI governance and ethics framework for sustainable AI and sustainability", venue: "Submission to Department of the Prime Minister and Cabinet", url: "https://doi.org/10.48550/arXiv.2210.08984" },
  { type: "blog", authors: [], year: 2021, title: "KITE: An abstraction framework for reducing complexity in AI governance", venue: "UN World Data Forum", url: "https://unstats.un.org/unsd/undataforum/blog/KITE-an-abstraction-framework-for-reducing-complexity-in-ai-governance" },
  { type: "magazine", authors: [], year: 2022, title: "Keeping AI honest", venue: "Company Director, 38(2), 52-53", url: "https://www.aicd.com.au/organisational-culture/business-ethics/issues/keeping-AI-honest.html" },
  { type: "commentary", authors: [], year: 2022, title: "Importance of AI governance toolkit for environmental, social and governance", venue: "Global Trends in AI 2022 - GAIEI", url: "https://globalethics.ai/global-trends-in-ai-2022-food-for-thought-from-gaiei-experts/" },
  { type: "journal", authors: [], year: 2023, title: "A conceptual design of an AI-enabled decision support system for analysing donor behaviour in nonprofit organisations", venue: "Information, 14(10), 578", url: "https://doi.org/10.3390/info14100578" },
  { type: "chapter", authors: [], year: 2022, title: "Analysing donors' behaviors in nonprofit organisations: A design science research framework", venue: "Proceedings of the 2022 Conference, 642-654", url: "https://link.springer.com/chapter/10.1007/978-981-19-1520-8_62" },
  { type: "preprint", authors: [], year: 2022, title: "Analysing donors' behaviour in nonprofit organisations for disaster resilience: The 2019-2020 Australian bushfires case study", venue: "arXiv", url: "https://doi.org/10.48550/arXiv.2210.09034" },
  { type: "preprint", authors: [], year: 2022, title: "The effectiveness of social media engagement strategy on disaster fundraising", venue: "arXiv", url: "https://doi.org/10.48550/arXiv.2210.11322" },
];

// ── Physics presentations (with YouTube) ─────────────────────────────────
const physicsPresentations = [
  {
    number: 1,
    title: "Unifying Matter, Energy and Consciousness",
    event: "11th International Conference on Mathematical Modeling in Physical Sciences",
    dates: "September 5-8, 2022",
    summary:
      "This work mathematically models the interplay of consciousness, matter and energy. It correlates the brain's electromagnetic energy to consciousness and presents consciousness as a sequential process related to a single state of reality at a given instant.",
    doi: "https://doi.org/10.1063/5.0162815",
    youtube: "https://www.youtube.com/embed/Pby1TfEluqE",
  },
  {
    number: 2,
    title: "Unifying Causation, Relativity and Quantum Mechanics based on Consciousness",
    event: "12th International Conference on Mathematical Modeling in Physical Sciences",
    dates: "August 28-31, 2023",
    summary:
      "This work argues that consciousness and change are fundamental to reality and govern causation. By replacing time with consciousness, the work explains the causation of relativity and enables consciousness to serve as a common basis for relativity and quantum mechanics.",
    doi: "https://doi.org/10.1088/1742-6596/2701/1/012051",
    youtube: "https://www.youtube.com/embed/HlguSExw5ew",
  },
  {
    number: 3,
    title: "Unifying Local and Non-Local Realities based on Consciousness",
    event: "12th International Conference on Mathematical Modeling in Physical Sciences",
    dates: "August 28-31, 2023",
    summary:
      "This work proposes that consciousness can unify local and non-local realities. It integrates consciousness with the standard model of particle physics to understand non-local reality in quantum entanglement.",
    doi: "https://doi.org/10.1088/1742-6596/2701/1/012110",
    youtube: "https://www.youtube.com/embed/65de5TNVUi0",
  },
];

// ── Media coverage ───────────────────────────────────────────────────────
const mediaCoverage = [
  { source: "SciMEX", title: "Unifying matter, energy and consciousness", summary: "This media release introduces the research as an attempt to better understand how consciousness may relate to the physical world.", url: "https://www.scimex.org/newsfeed/unifying-matter-energy-and-consciousness" },
  { source: "UTS News", title: "Unifying matter, energy and consciousness", summary: "This UTS article presents the research as a cross-disciplinary effort linking physics, neuroscience, engineering, and artificial intelligence.", url: "https://www.uts.edu.au/news/2023/10/unifying-matter-energy-and-consciousness" },
  { source: "UTS Feature", title: "Can matter and energy blend with awareness?", summary: "This short feature explains the research in a public-facing way, focusing on why the questions are urgent today as AI and brain-interface technologies develop.", url: "https://www.uts.edu.au/news/2023/10/can-matter-and-energy-blend-awareness" },
  { source: "GrowKudos", title: "Unifying Matter, Energy and Consciousness to Understand the Fundamentals of Reality", summary: "Plain-language research summary outlining the core idea: consciousness is treated as a fundamental aspect of nature that interacts with the brain, matter, and energy.", url: "https://www.growkudos.com/publications/10.1063%25252F5.0162815/reader" },
];

const videoCategoryLabels = {
  introduction: "Introduction",
  research_overview: "Research Overview",
  keynote: "Keynote",
  interview: "Interview",
};

// ── Publication card ─────────────────────────────────────────────────────
const PublicationCard = ({ pub }) => {
  const typeColors = {
    journal: "bg-blue-100 text-blue-700",
    conference: "bg-purple-100 text-purple-700",
    website: "bg-emerald-100 text-emerald-700",
    submission: "bg-amber-100 text-amber-700",
    preprint: "bg-slate-100 text-slate-700",
    blog: "bg-rose-100 text-rose-700",
    magazine: "bg-cyan-100 text-cyan-700",
    chapter: "bg-indigo-100 text-indigo-700",
    commentary: "bg-orange-100 text-orange-700",
  };

  return (
    <Card className="bg-white border border-slate-200 hover:shadow-md transition-shadow h-full">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  typeColors[pub.type] || typeColors.preprint
                }`}
              >
                {pub.type}
              </span>
              {pub.year && <span className="text-xs text-slate-500">{pub.year}</span>}
            </div>
            <h4 className="font-serif text-base text-slate-900 mb-1 leading-snug">
              {pub.title}
            </h4>
            {pub.authors && pub.authors.length > 0 && (
              <p className="text-sm text-slate-500 mb-2">{pub.authors.join(", ")}</p>
            )}
            <p className="text-xs text-slate-600">{pub.venue}</p>
          </div>
          {pub.url && (
            <a
              href={pub.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 p-2 text-slate-400 hover:text-brand-primary hover:bg-slate-50 rounded-lg transition-colors"
              aria-label="View publication"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Presentation card (inline video poster + DOI) ────────────────────────
const PresentationCard = ({ pres, onPlay }) => (
  <Card className="bg-slate-800 border border-slate-700 overflow-hidden h-full flex flex-col">
    <div
      className="relative aspect-video bg-slate-900 cursor-pointer group"
      onClick={() => onPlay({ title: pres.title, video_url: pres.youtube, description: pres.summary })}
    >
      <img
        src={`https://img.youtube.com/vi/${pres.youtube.split("/").pop()}/maxresdefault.jpg`}
        alt={pres.title}
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
      />
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
          <Play className="w-7 h-7 text-slate-900 ml-1" />
        </div>
      </div>
      <span className="absolute top-2 left-2 bg-purple-500/80 text-white text-xs px-2 py-1 rounded font-mono">
        Presentation {pres.number}
      </span>
    </div>
    <CardContent className="p-5 flex-1 flex flex-col">
      <h4 className="font-serif text-base text-white mb-2 leading-snug">
        {pres.title}
      </h4>
      <p className="text-xs text-slate-400 mb-3">
        {pres.event} · {pres.dates}
      </p>
      <p className="text-sm text-slate-300 leading-relaxed mb-3 line-clamp-4 flex-1">
        {pres.summary}
      </p>
      <a
        href={pres.doi}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-blue-300 hover:underline"
      >
        View DOI <ExternalLink className="w-3 h-3" />
      </a>
    </CardContent>
  </Card>
);

// ══════════════════════════════════════════════════════════════════════════
// Publications Page (consolidated outputs)
// ══════════════════════════════════════════════════════════════════════════
export const PublicationsPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/videos`)
      .then((res) => setVideos(res.data))
      .catch((err) => console.error("Error fetching videos:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div id="main-content" className="pt-20" data-testid="publications-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-brand-primary mb-4">Research Outputs</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight mb-6">
              Publications, Presentations, Videos &amp; Media
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
              A curated record of the Centre's peer-reviewed publications, conference presentations, public interviews, and press coverage across consciousness, AI governance, neurotechnology, and fundamental physics.
            </p>
          </div>
        </div>
      </section>

      {/* Consciousness Research Publications */}
      <section
        className="py-16 lg:py-24 bg-slate-50"
        data-testid="consciousness-publications"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Brain className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl lg:text-3xl text-slate-900">
                Consciousness Research
              </h2>
              <p className="text-slate-500">
                Publications exploring consciousness, causation, and reality
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {consciousnessPublications.map((pub, idx) => (
              <PublicationCard key={idx} pub={pub} />
            ))}
          </div>
        </div>
      </section>

      {/* AI Governance Publications */}
      <section
        className="py-16 lg:py-24 bg-white"
        data-testid="ai-governance-publications"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Cpu className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl lg:text-3xl text-slate-900">
                Human-Centred AI: Implications for AI Governance, Ethics and Safety
              </h2>
              <p className="text-slate-500">
                Publications on human-centred AI, governance frameworks, ethics, and safety
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {aiGovernancePublications.map((pub, idx) => (
              <PublicationCard key={idx} pub={pub} />
            ))}
          </div>
        </div>
      </section>

      {/* Fundamental Physics Presentations */}
      <section
        id="presentations"
        className="py-16 lg:py-24 bg-slate-900 text-white scroll-mt-24"
        data-testid="physics-presentations"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Atom className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="font-serif text-2xl lg:text-3xl text-white">
                Fundamental Physics Presentations
              </h2>
              <p className="text-slate-400">
                Conference presentations with video recordings
              </p>
            </div>
          </div>
          <p className="text-slate-300 max-w-4xl mb-10 leading-relaxed">
            Consciousness is a fundamental dimension of reality that governs causation. By accounting for consciousness, both relativity and quantum physics can be unified. Our research promotes a deep dive into consciousness, exploring it across the STEM fields.
          </p>
          <div className="grid lg:grid-cols-3 gap-6">
            {physicsPresentations.map((pres, idx) => (
              <PresentationCard key={idx} pres={pres} onPlay={setSelectedVideo} />
            ))}
          </div>
        </div>
      </section>

      {/* Videos & Interviews (API-driven) */}
      <section
        id="videos"
        className="py-16 lg:py-24 bg-white scroll-mt-24"
        data-testid="videos-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-lg bg-rose-100 flex items-center justify-center">
              <Play className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl lg:text-3xl text-slate-900">
                Videos &amp; Interviews
              </h2>
              <p className="text-slate-500">
                Research introductions, keynotes, and interviews
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="bg-slate-100 rounded-lg aspect-video animate-pulse"
                ></div>
              ))}
            </div>
          ) : videos.length === 0 ? (
            <p className="text-slate-500 text-center py-12">
              No videos available yet.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video, idx) => (
                <Card
                  key={video.id}
                  className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`video-item-${idx}`}
                >
                  <div className="relative aspect-video bg-slate-200">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-7 h-7 text-slate-900 ml-1" />
                      </div>
                    </div>
                    {video.duration && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono">
                        {video.duration}
                      </span>
                    )}
                    {video.featured && (
                      <span className="absolute top-2 left-2 bg-brand-primary text-white text-xs px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <span className="tag tag-primary text-xs mb-2 inline-block">
                      {videoCategoryLabels[video.category] ||
                        video.category.replace("_", " ")}
                    </span>
                    <h3 className="font-serif text-lg text-slate-900 mb-2 line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Media Coverage */}
      <section
        id="media"
        className="py-16 lg:py-24 bg-slate-50 scroll-mt-24"
        data-testid="media-coverage"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl lg:text-3xl text-slate-900">
                Media Coverage
              </h2>
              <p className="text-slate-500">
                Press coverage and public-facing research summaries
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {mediaCoverage.map((item, idx) => (
              <Card
                key={idx}
                className="bg-white border border-slate-200 hover:shadow-md transition-shadow"
              >
                <CardContent className="p-6">
                  <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider">
                    {item.source}
                  </span>
                  <h4 className="font-serif text-lg text-slate-900 mt-2 mb-3">
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">{item.summary}</p>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-brand-primary hover:underline"
                  >
                    Read Article <ExternalLink className="w-4 h-4" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Shared video modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent
          className="max-w-4xl p-0 overflow-hidden"
          data-testid="video-modal"
        >
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="font-serif text-xl">
              {selectedVideo?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black">
            {selectedVideo && (
              <iframe
                src={selectedVideo.video_url}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
          {(selectedVideo?.description || selectedVideo?.summary) && (
            <div className="p-4">
              <p className="text-sm text-slate-600">
                {selectedVideo?.description || selectedVideo?.summary}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
            Interested in Our Research?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            Explore our research themes and approach, or get in touch to discuss collaboration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="explore-research-btn"
            >
              <Link to="/research">
                Explore Research Themes <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-slate-300"
              data-testid="contact-btn"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicationsPage;
